import DoorImage from '../components/DoorImage';
import React, { useEffect, useState } from "react";
import { Link, useSearchParams, Navigate, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, User, Trash2, Plus, Minus, MessageCircle, LogOut, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { useLang, localized } from "../lib/i18n";
import { useSettings, whatsappUrl, pickLoc } from "../lib/settings";
import { api, FILE_URL } from "../lib/api";

const DEFAULT_DOOR = "https://images.unsplash.com/photo-1659720879327-827462ca3942?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsJTIwaW50ZXJpb3IlMjBkb29yfGVufDB8fHx8MTc4NDE0NzE2Mnww&ixlib=rb-4.1.0&q=85";

export default function Profile() {
  const { t, lang } = useLang();
  const { user, loading, logout, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const tab = params.get("tab") || "info";

  if (loading) return <Layout><div className="fd-container py-24 text-center text-neutral-400">{t("common.loading")}</div></Layout>;
  if (!user) return <Navigate to="/login" state={{ from: "/profile" }} replace />;

  const setTab = (v) => setParams({ tab: v });

  const tabs = [
    { key: "info", label: t("auth.profile"), icon: User },
    { key: "favorites", label: t("auth.favorites"), icon: Heart },
    { key: "cart", label: t("auth.cart"), icon: ShoppingBag },
  ];

  return (
    <Layout>
      <section className="fd-container py-8 md:py-12 border-b border-neutral-200">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="fd-label mb-2">— {t("auth.my_account")}</p>
            <h1 className="text-3xl md:text-5xl font-light tracking-tight text-neutral-900">{user.name} {user.surname}</h1>
            <p className="text-sm text-neutral-500 mt-2">{user.email}</p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Link to="/admin" className="fd-btn-ghost text-xs" data-testid="profile-admin-link">
                <LayoutDashboard size={14} /> {t("auth.admin_panel")}
              </Link>
            )}
            <button onClick={() => { logout(); nav("/"); }} className="fd-btn-ghost text-xs" data-testid="profile-logout">
              <LogOut size={14} /> {t("auth.logout")}
            </button>
          </div>
        </div>
        <div className="flex gap-1 mt-8 overflow-x-auto">
          {tabs.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`tab-${tb.key}`}
              className={`px-6 py-3 text-sm border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                tab === tb.key ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-400 hover:text-neutral-700"
              }`}>
              <tb.icon size={14} /> {tb.label}
            </button>
          ))}
        </div>
      </section>

      <section className="fd-container py-8 md:py-12">
        {tab === "info" && <InfoTab />}
        {tab === "favorites" && <FavoritesTab />}
        {tab === "cart" && <CartTab />}
      </section>
    </Layout>
  );
}

function InfoTab() {
  const { user } = useAuth();
  const { t } = useLang();
  return (
    <div className="max-w-xl space-y-6" data-testid="tab-info-content">
      <div className="border border-neutral-200 p-6 space-y-4">
        <Field label={t("auth.name")} value={user.name} />
        <Field label={t("auth.surname")} value={user.surname} />
        <Field label={t("auth.email")} value={user.email} />
        <Field label={t("auth.role")} value={user.role === "admin" ? "Admin" : t("auth.customer")} />
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="fd-label mb-1">{label}</p>
      <p className="text-neutral-900">{value || "—"}</p>
    </div>
  );
}

function FavoritesTab() {
  const { t, lang } = useLang();
  const { user, favorites, toggleFavorite, addToCart } = useAuth();
  const [doors, setDoors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (user) {
          const r = await api.get("/me/favorites");
          setDoors(r.data);
        } else if (favorites.length) {
          // fallback: fetch each door
          const all = await api.get("/doors").then((r) => r.data);
          setDoors(all.filter((d) => favorites.includes(d.id)));
        } else {
          setDoors([]);
        }
      } finally { setLoading(false); }
    };
    load();
  }, [user, favorites.join(",")]);

  if (loading) return <p className="text-neutral-400 text-sm">{t("common.loading")}</p>;
  if (doors.length === 0) return (
    <div className="border border-dashed border-neutral-200 py-16 text-center text-sm text-neutral-400" data-testid="favorites-empty">
      {t("auth.favorites_empty")} <Link to="/catalog" className="text-neutral-900 underline ml-1">{t("nav.catalog")}</Link>
    </div>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-list">
      {doors.map((d) => (
        <div key={d.id} className="group border border-neutral-200 fd-hover-lift" data-testid={`fav-item-${d.id}`}>
          <Link to={`/doors/${d.id}`} className="block aspect-[3/4] overflow-hidden bg-neutral-100">
            <DoorImage src={d.image_id ? FILE_URL(d.image_id) : DEFAULT_DOOR} alt={localized(d.name, lang)} fallback={DEFAULT_DOOR} />
          </Link>
          <div className="p-4 flex items-start justify-between">
            <div>
              <p className="fd-label mb-1">{t(`categories.${d.category}`)}</p>
              <Link to={`/doors/${d.id}`} className="text-neutral-900 hover:underline">{localized(d.name, lang)}</Link>
              <p className="text-neutral-500 text-sm mt-1">{d.price} {t("common.currency")}</p>
            </div>
            <button onClick={() => toggleFavorite(d.id)} className="p-2 text-red-600 hover:bg-red-50" title={t("auth.remove")} data-testid={`fav-remove-${d.id}`}>
              <Heart size={16} fill="currentColor" />
            </button>
          </div>
          <div className="px-4 pb-4">
            <button onClick={() => { addToCart(d.id); toast.success(t("cart.added")); }} className="fd-btn-ghost w-full justify-center text-xs" data-testid={`fav-add-cart-${d.id}`}>
              <ShoppingBag size={14} /> {t("cart.add")}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CartTab() {
  const { t, lang } = useLang();
  const { user, cart, setCartQty, removeFromCart, clearCart } = useAuth();
  const { settings } = useSettings();
  const [items, setItems] = useState([]); // [{door, qty}]
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      if (user) {
        const r = await api.get("/me/cart");
        setItems(r.data);
      } else if (cart.length) {
        const all = await api.get("/doors").then((r) => r.data);
        const byId = Object.fromEntries(all.map((d) => [d.id, d]));
        setItems(cart.map((c) => ({ door: byId[c.door_id], qty: c.qty })).filter((x) => x.door));
      } else {
        setItems([]);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [user, cart.map((c) => c.door_id + ":" + c.qty).join(",")]);

  const total = items.reduce((s, it) => s + (Number(it.door.price) || 0) * it.qty, 0);

  const buildMessage = () => {
    const lines = [t("cart.wa_greeting")];
    items.forEach((it, i) => {
      lines.push(`${i + 1}. ${localized(it.door.name, lang)} — ${it.qty} × ${it.door.price} ${t("common.currency")}`);
    });
    lines.push("");
    lines.push(`${t("cart.total")}: ${total} ${t("common.currency")}`);
    if (user) lines.push(`${t("auth.name")}: ${user.name} ${user.surname}`);
    return lines.join("\n");
  };

  const orderViaWhatsApp = () => {
    if (items.length === 0) return;
    const url = whatsappUrl(settings?.whatsapp, buildMessage());
    window.open(url, "_blank");
  };

  if (loading) return <p className="text-neutral-400 text-sm">{t("common.loading")}</p>;
  if (items.length === 0) return (
    <div className="border border-dashed border-neutral-200 py-16 text-center text-sm text-neutral-400" data-testid="cart-empty">
      {t("cart.empty")} <Link to="/catalog" className="text-neutral-900 underline ml-1">{t("nav.catalog")}</Link>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="cart-content">
      <div className="lg:col-span-2 space-y-3">
        {items.map(({ door, qty }) => (
          <div key={door.id} className="flex gap-4 border border-neutral-200 p-4" data-testid={`cart-item-${door.id}`}>
            <Link to={`/doors/${door.id}`} className="w-24 h-28 bg-neutral-100 overflow-hidden shrink-0">
              <DoorImage src={door.image_id ? FILE_URL(door.image_id) : DEFAULT_DOOR} alt="" fallback={DEFAULT_DOOR} />
            </Link>
            <div className="flex-1 min-w-0">
              <p className="fd-label mb-1">{t(`categories.${door.category}`)}</p>
              <Link to={`/doors/${door.id}`} className="text-neutral-900 hover:underline">{localized(door.name, lang)}</Link>
              <p className="text-sm text-neutral-500 mt-1">{door.price} {t("common.currency")} × {qty} = <span className="text-neutral-900">{door.price * qty} {t("common.currency")}</span></p>
              <div className="flex items-center gap-3 mt-3">
                <div className="inline-flex items-center border border-neutral-300">
                  <button onClick={() => setCartQty(door.id, Math.max(1, qty - 1))} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100" data-testid={`cart-dec-${door.id}`} aria-label="decrease"><Minus size={14} /></button>
                  <span className="px-3 text-sm min-w-[32px] text-center" data-testid={`cart-qty-${door.id}`}>{qty}</span>
                  <button onClick={() => setCartQty(door.id, qty + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100" data-testid={`cart-inc-${door.id}`} aria-label="increase"><Plus size={14} /></button>
                </div>
                <button onClick={() => removeFromCart(door.id)} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1" data-testid={`cart-remove-${door.id}`}>
                  <Trash2 size={12} /> {t("auth.remove")}
                </button>
              </div>
            </div>
          </div>
        ))}
        <button onClick={() => { if (window.confirm(t("cart.confirm_clear"))) clearCart(); }} className="text-xs text-neutral-500 hover:text-red-600 mt-4" data-testid="cart-clear">
          {t("cart.clear")}
        </button>
      </div>
      <aside className="border border-neutral-200 p-6 h-fit lg:sticky lg:top-24" data-testid="cart-summary">
        <p className="fd-label mb-4">— {t("cart.summary")}</p>
        <div className="flex justify-between text-sm text-neutral-500 mb-2">
          <span>{t("cart.items")}</span>
          <span>{items.reduce((s, it) => s + it.qty, 0)}</span>
        </div>
        <div className="flex justify-between text-lg text-neutral-900 mb-6 border-t border-neutral-200 pt-4">
          <span>{t("cart.total")}</span>
          <span>{total} {t("common.currency")}</span>
        </div>
        <button onClick={orderViaWhatsApp} className="fd-btn-primary w-full justify-center !bg-[#25D366] hover:!bg-[#1ebe57]" data-testid="cart-whatsapp">
          <MessageCircle size={16} /> {t("cart.order_wa")}
        </button>
        <p className="text-[11px] text-neutral-400 mt-3 leading-relaxed">{t("cart.wa_note")}</p>
      </aside>
    </div>
  );
}
